<template>
  <div class="border rounded p-3">
    <!-- Cabecera: identidad + acciones -->
    <div class="dep-card-header">
      <div class="flex items-center">
        <div class="dep-avatar mr-3">{{ initials }}</div>
        <div>
          <strong>{{ dependent.fullName }}</strong>
          <wz-badge variant="info-solid" class="ml-2">{{ relationshipLabel }}</wz-badge>
        </div>
      </div>
      <div class="dep-card-actions">
        <wz-button v-if="!draft" variant="light" size="sm" class="mr-2" @click="$emit('docs', dependent)">
          <i class="ri-attachment-2 mr-1"></i>Documentos
        </wz-button>
        <wz-button variant="light" size="sm" class="mr-2" @click="$emit('edit', dependent)">Editar</wz-button>
        <wz-button variant="light" size="sm" class="text-danger" @click="$emit('deactivate', dependent)">
          {{ draft ? 'Quitar' : 'Desactivar' }}
        </wz-button>
      </div>
    </div>

    <!-- Datos: grilla responsiva (no se encima en anchos chicos) -->
    <div class="dep-info-grid text-muted text-sm">
      <div class="dep-info-item">
        <span class="dep-info-label">Documento</span>
        <strong class="text-gray-900">{{ docTypeLabel }} {{ dependent.documentNumber }}</strong>
      </div>
      <div class="dep-info-item">
        <span class="dep-info-label">Fecha nacimiento</span>
        <strong class="text-gray-900 dep-info-nowrap"><span v-local-date="dependent.birthDate"></span> — {{ dependent.age }} años</strong>
      </div>
      <div class="dep-info-item" v-if="dependent.gender">
        <span class="dep-info-label">Género</span>
        <strong class="text-gray-900">{{ dependent.gender === 'M' ? 'Masculino' : 'Femenino' }}</strong>
      </div>

      <!-- Campos específicos de HIJO -->
      <template v-if="showChildFields">
        <div class="dep-info-item">
          <span class="dep-info-label">Constancia estudios</span>
          <strong v-if="!dependent.hasStudyCertificate" class="text-gray-900">-</strong>
          <strong v-else :class="certificateClass">{{ certificateText }}</strong>
        </div>
        <div class="dep-info-item">
          <span class="dep-info-label">Asignación familiar</span>
          <wz-badge v-if="dependent.familyAllowanceStatus" :variant="familyAllowanceDisplay.variant">
            {{ familyAllowanceDisplay.label }}
          </wz-badge>
          <strong v-else class="text-gray-900">-</strong>
        </div>
      </template>

      <div class="dep-info-item" v-if="dependent.supportingDocumentType">
        <span class="dep-info-label">Doc. sustentatorio</span>
        <strong class="text-gray-900">
          {{ dependent.supportingDocumentType }}{{ dependent.supportingDocumentNumber ? ' ' + dependent.supportingDocumentNumber : '' }}
        </strong>
      </div>
      <div class="dep-info-item" v-if="dependent.notes">
        <span class="dep-info-label">Observaciones</span>
        <strong class="text-gray-900">{{ dependent.notes }}</strong>
      </div>
    </div>
  </div>
</template>

<script>
import { Constants } from '@/core/utils'

export default {
  name: 'DependentCard',
  props: {
    dependent: { type: Object, required: true },
    // true → muestra Constancia de estudios y Estado AF (solo aplican a hijos).
    showChildFields: { type: Boolean, default: false },
    // true (modo borrador, alta) → oculta "Documentos" y renombra "Desactivar" a "Quitar".
    draft: { type: Boolean, default: false },
  },
  computed: {
    initials() {
      const f = (this.dependent.firstName || '').trim()
      const l = (this.dependent.lastNamePaternal || '').trim()
      return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?'
    },
    relationshipLabel() {
      const found = Object.values(Constants.RelationshipType).find(r => r.key === this.dependent.relationshipType)
      return found ? found.label : this.dependent.relationshipType
    },
    docTypeLabel() {
      const found = Object.values(Constants.IdentifierType).find(t => String(t.key) === String(this.dependent.documentType))
      return found ? found.label : this.dependent.documentType
    },
    // Badge claro de asignación familiar por hijo: verde = genera, gris = no genera, ámbar = genera pero
    // con constancia por vencer. Evita el confuso "Estado AF: No califica" y no depende del catálogo de labels.
    familyAllowanceDisplay() {
      switch (this.dependent.familyAllowanceStatus) {
        case 'CALIFICA':
          return { label: 'Genera AF', variant: 'success' }
        case 'CONSTANCIA_POR_VENCER':
          return { label: 'Genera AF (constancia por vencer)', variant: 'warning' }
        default:
          return { label: 'No genera AF', variant: 'secondary' }
      }
    },
    certificateText() {
      const exp = this.dependent.studyCertificateExpiry
      if (!exp) return 'Sin fecha'
      const expiry = new Date(exp + 'T00:00:00')
      const today = new Date()
      if (expiry < today) return 'Vencida'
      const diffDays = Math.round((expiry - today) / (1000 * 60 * 60 * 24))
      if (diffDays <= 30) return `Vence ${exp}`
      return `Hasta ${exp}`
    },
    certificateClass() {
      const exp = this.dependent.studyCertificateExpiry
      if (!exp) return 'text-muted'
      const expiry = new Date(exp + 'T00:00:00')
      const today = new Date()
      if (expiry < today) return 'text-danger font-weight-bold'
      const diffDays = Math.round((expiry - today) / (1000 * 60 * 60 * 24))
      if (diffDays <= 30) return 'text-warning font-weight-bold'
      return 'text-success'
    },
  },
}
</script>

<style scoped>
.dep-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #4f46e5;
  color: white;
  font-size: 0.875rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Cabecera: identidad a la izquierda, acciones a la derecha. Con wrap para que en anchos chicos
   las acciones bajen en lugar de comprimir los datos. */
.dep-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.dep-card-actions {
  display: flex;
  flex-shrink: 0;
}

/* Grilla de datos responsiva: columnas que nunca se enciman (mínimo 180px, se acomodan solas). */
.dep-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 16px;
}

.dep-info-label {
  display: block;
  margin-bottom: 2px;
}

/* Evita el wrap raro de "DD/MM/AAAA — NN años". */
.dep-info-nowrap {
  white-space: nowrap;
}
</style>
